"use client"

import React, { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import theme from "tailwindcss/defaultTheme"
import { useIsMounted, useMediaQuery } from "usehooks-ts"

import { cn } from "@/components/ui/helpers/utils"
import HtmlTextBox from "@/components/storyblok/common/HtmlTextBox"

interface CategoryDescriptionProps extends Omit<React.HTMLProps<HTMLDivElement>, "children"> {
  text?: string
  children?: React.ReactNode
  contentAnchor: string
}

export const CategoryDescriptionClient: React.FC<CategoryDescriptionProps> = ({
  text,
  children,
  contentAnchor,
  className,
  ...restProps
}) => {
  const isMD = useMediaQuery(`(min-width: ${theme.screens.md}`)
  const [collapsed, setCollapsed] = useState<boolean | null>(null)

  const isMounted = useIsMounted()
  const router = useRouter()
  const t = useTranslations("storefront")

  useEffect(() => {
    if (isMounted()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCollapsed(!isMD)
    }
  }, [isMD, isMounted])

  return (
    <div className={cn("flex flex-col gap-2", className)} {...restProps}>
      {children ? (
        <div className={cn("relative prose prose-sm max-w-full", { "line-clamp-2": collapsed })}>{children}</div>
      ) : (
        <HtmlTextBox
          classname={cn("line-clamp-none", {
            "line-clamp-2": collapsed,
          })}
          text={text ?? ""}
        />
      )}
      <div className={cn("flex flex-row justify-between gap-2")}>
        <button
          className={cn("p-0 text-sm underline")}
          onClick={() => {
            if (collapsed) {
              setCollapsed(false)
            } else {
              router.push("#" + contentAnchor)
            }
          }}
        >
          {t("category.more")}
        </button>
        <button
          className={cn("p-0 text-sm underline md:hidden", { hidden: collapsed })}
          onClick={(e) => {
            setCollapsed(true)
            if (isMD) {
              return
            }
            const btn = e.currentTarget
            setTimeout(() => btn?.parentElement?.parentElement?.scrollIntoView({ behavior: "smooth" }), 50)
          }}
        >
          {t("category.less")}
        </button>
      </div>
    </div>
  )
}
